import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const OCCASIONS = ["Weddings", "Bridal Showers", "Baby Showers", "Birthdays", "Church Events", "Women's Events", "Corporate Events", "Special Occasions"];
const INTERESTS = ["Custom Scents", "Custom Labels", "Bulk Orders", "Personalized Candles", "Event Favors"];
const EMPTY = { name: "", email: "", phone: "", occasion: "", event_date: "", quantity: "", details: "" };

const Field = ({ label, children, testid }) => (
  <label className="block" data-testid={testid}>
    <span className="mb-2 block font-mono text-[10px] uppercase tracking-[0.28em] text-stone2">{label}</span>
    {children}
  </label>
);

const Favors = () => {
  const [form, setForm] = useState(EMPTY);
  const [interests, setInterests] = useState([]);
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const toggleInterest = (i) => setInterests((prev) => prev.includes(i) ? prev.filter((x) => x !== i) : [...prev, i]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, { type: "favors", data: { ...form, interests } });
      setSent(true);
      toast.success("Your favor request has been sent");
    } catch {
      toast.error("Something went wrong — please try again");
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="favors-page" className="bg-ivory">
      <section className="border-b border-line py-16 md:py-24 text-center">
        <Reveal>
          <p className="eyebrow">Custom Candle Favors</p>
          <h1 className="mx-auto mt-5 max-w-3xl font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight">
            Made Especially for <em>Your Moment</em>
          </h1>
          <p className="mx-auto mt-8 max-w-xl text-sm md:text-base leading-relaxed text-stone2">
            Hand-poured soy wax candle favors, personalized for the occasions your guests will never forget.
          </p>
        </Reveal>
      </section>
      <section className="mx-auto max-w-7xl px-6 sm:px-12 lg:px-20 py-16">
        <Reveal className="flex flex-wrap justify-center gap-2">
          {OCCASIONS.map((o) => (
            <span key={o} data-testid={`favor-occasion-${o.toLowerCase().replace(/[\s']/g, "-")}`} className="border border-line bg-white px-5 py-2.5 text-[10px] uppercase tracking-[0.22em] text-stone2">{o}</span>
          ))}
        </Reveal>
        <div className="mx-auto mt-16 max-w-3xl">
          {sent ? (
            <Reveal className="border border-line bg-white p-12 text-center" data-testid="favors-success">
              <p className="font-serif text-3xl font-light italic">Consider it poured with love.</p>
              <p className="mt-4 text-sm text-stone2 leading-relaxed">Your custom favor request has been received. We'll reach out within 2-3 business days with options and pricing.</p>
              <button onClick={() => { setSent(false); setForm(EMPTY); setInterests([]); }} data-testid="favors-request-another" className="btn-outline mt-8">Request Another</button>
            </Reveal>
          ) : (
            <Reveal delay={0.1}>
              <form onSubmit={submit} data-testid="custom-favors-form" className="border border-line bg-white p-8 md:p-12 grid gap-6 sm:grid-cols-2">
                <Field label="Name" testid="field-favor-name"><input required data-testid="favor-name-input" value={form.name} onChange={set("name")} placeholder="Your full name" /></Field>
                <Field label="Email" testid="field-favor-email"><input required type="email" data-testid="favor-email-input" value={form.email} onChange={set("email")} placeholder="you@email.com" /></Field>
                <Field label="Phone" testid="field-favor-phone"><input data-testid="favor-phone-input" value={form.phone} onChange={set("phone")} placeholder="(555) 000-0000" /></Field>
                <Field label="Occasion" testid="field-favor-occasion">
                  <select required data-testid="favor-occasion-select" value={form.occasion} onChange={set("occasion")}>
                    <option value="">Select an occasion…</option>
                    {OCCASIONS.map((o) => <option key={o}>{o}</option>)}
                  </select>
                </Field>
                <Field label="Event Date" testid="field-favor-date"><input type="date" data-testid="favor-date-input" value={form.event_date} onChange={set("event_date")} /></Field>
                <Field label="Estimated Quantity" testid="field-favor-quantity"><input type="number" min="1" data-testid="favor-quantity-input" value={form.quantity} onChange={set("quantity")} placeholder="e.g. 50" /></Field>
                <div className="sm:col-span-2" data-testid="field-favor-interests">
                  <span className="mb-3 block font-mono text-[10px] uppercase tracking-[0.28em] text-stone2">I'm Interested In</span>
                  <div className="flex flex-wrap gap-2">
                    {INTERESTS.map((i) => (
                      <button type="button" key={i} data-testid={`interest-${i.toLowerCase().replace(/\s+/g, "-")}`} onClick={() => toggleInterest(i)}
                        className={`px-5 py-2.5 text-[10px] font-medium uppercase tracking-[0.22em] border transition-all duration-300 ${interests.includes(i) ? "border-ink bg-ink text-ivory" : "border-line bg-white text-stone2 hover:border-ink hover:text-ink"}`}>
                        {i}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="sm:col-span-2"><Field label="Additional Details" testid="field-favor-details"><textarea rows="4" data-testid="favor-details-input" value={form.details} onChange={set("details")} placeholder="Colors, themes, label wording, fragrance ideas…" /></Field></div>
                <div className="sm:col-span-2">
                  <button type="submit" disabled={loading} data-testid="custom-favors-submit" className="btn-primary w-full disabled:opacity-50">
                    {loading ? "Sending…" : "Request Custom Favors"}
                  </button>
                </div>
              </form>
            </Reveal>
          )}
        </div>
      </section>
    </div>
  );
};

export default Favors;
